'use client'

import * as React from 'react'
import Masonry from 'react-masonry-css'
import { Divider } from 'components/divider'
import { useTranslations } from 'next-intl'
import { ChevronDownIcon, SearchIcon } from 'primitives/icons'

import { SparkBanner } from './spark-components'
import { BUTTON_FILTERS } from '@web/app/[locale]/(web)/search/_components/sidebar/class-search-sidebar'
import { ProductCardSkeleton } from '@web/components/product-card/product-card'

export function SearchSkeleton({ isClasses = false }: { isClasses?: boolean }) {
  return (
    <div className="flex w-full justify-center bg-[#f6f7fb]">
      <div className="bg-gray-bg flex w-full flex-col items-center justify-center">
        <section className="flex w-full flex-col md:flex-row">
          <div className="w-full px-5 py-12 pb-4 md:mt-9 md:w-[22.5rem] md:min-w-[22.5rem] md:pl-10 md:pr-6 md:pt-3">
            <SidebarSkeleton isClasses={isClasses} />
          </div>
          <div className="w-full min-w-0 px-5 md:py-[30px] md:pl-1 md:pr-10 md:pt-12">
            <SearchResultsSkeleton isClasses={isClasses} />
          </div>
        </section>
      </div>
    </div>
  )
}

export function SidebarSkeleton({ isClasses }: { isClasses: boolean }) {
  const t = useTranslations('pages.search')

  return (
    <div className="bg-cf-white rounded text-gray-100">
      <h3 className="text-13 p-5 font-bold">{t('filter.search_and_filter')}</h3>
      <Divider className="h-[3px] bg-gray-700" />
      <div className="flex flex-col gap-5 p-5">
        <div className="relative flex w-full flex-col">
          <div className="relative flex w-full items-center">
            <input
              className="placeholder-transition h-[40px] w-full rounded-[6px] border border-gray-700 bg-gray-900 px-5 py-[10px] pr-10 text-gray-50 placeholder:text-gray-700 focus:outline-none"
              type="text"
              placeholder={t('placeholder')}
            />

            <div className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-gray-200">
              <SearchIcon aria-label="search" className="size-4" />
            </div>
          </div>
        </div>
        <Divider className="relative left-[-20px] w-[calc(100%+40px)] bg-gray-700" />
        {isClasses ? (
          <>
            <div className="flex justify-evenly gap-[10px]">
              {BUTTON_FILTERS.map(filter => (
                <button
                  className="text-cf-white h-[30px] w-full rounded bg-[#0177FB] font-semibold uppercase hover:bg-[#1e3892]"
                  key={filter}
                >
                  {t(`classes.search_${filter}`)}
                </button>
              ))}
            </div>
            <Divider className="relative left-[-20px] w-[calc(100%+40px)] bg-gray-700" />
          </>
        ) : null}

        <div className="flex h-[40px] items-center justify-between">
          <div className="h-[20px] w-[150px] animate-pulse rounded bg-gray-700" />
          <ChevronDownIcon className="h-[16px] w-[16px] text-gray-500" aria-hidden />
        </div>
      </div>
    </div>
  )
}

export function SearchResultsSkeleton({ isClasses }: { isClasses: boolean }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex animate-pulse items-center justify-between">
        <div className="bg-cf-dreamy-cloud-500 h-[20px] w-[200px] rounded" />
        <div className="bg-cf-dreamy-cloud-500 h-[42px] w-[150px] rounded" />
      </div>
      {!isClasses ? <SparkBanner query="" /> : undefined}
      <Masonry
        className="mt-5 flex gap-[10px]"
        columnClassName="flex flex-col gap-[10px] min-w-0"
        breakpointCols={{ 768: 1, 1024: 2, 1280: 3, default: 4 }}
      >
        {Array.from({ length: 16 }).map((_, index) => (
          <div key={`search_result_${index}`}>
            <ProductCardSkeleton />
          </div>
        ))}
      </Masonry>
    </div>
  )
}
